import { memo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Navbar.module.css";
import { logoutUser } from "../../Auth/authSlice";

const NAV_LINKS = [
  { id: "home", label: "navbar.home", isPage: false },
  { id: "about", label: "navbar.about", isPage: false },
  { id: "services", label: "navbar.services", isPage: false },
  { id: "categories", label: "navbar.categories", isPage: false },
  { id: "podcasts", label: "navbar.podcasts", isPage: true, path: "/podcasts" },
  { id: "radio", label: "navbar.radio", isPage: true, path: "/radio" },
  { id: "terms", label: "navbar.terms", isPage: true, path: "/terms" },
  { id: "privacy", label: "navbar.privacy", isPage: true, path: "/privacy" },
  { id: "contact", label: "navbar.contact", isPage: false },
];

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [menuOpen, setMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const cartItemsCount = useSelector((state) => {
    var cart = state.cart.cart;
    return cart && cart.items ? cart.items.length : 0;
  });

  var currentLanguage = (i18n.language || "en").split("-")[0];

  const handleLogout = () => {
    setMenuOpen(false);
    dispatch(logoutUser(currentLanguage)).then(() => {
      navigate("/");
    });
  };

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const lang = i18n.language;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    localStorage.setItem("lang", lang);
  }, [i18n.language]);

  const handleLinkClick = useCallback(
    (e, link) => {
      setMenuOpen(false);

      if (link.isPage) {
        return;
      }

      e.preventDefault();
      const scrollToElement = () => {
        const element = document.getElementById(link.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      };

      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(scrollToElement, 300); 
      } else {
        scrollToElement();
      }
    },
    [location.pathname, navigate],
  );

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setMenuOpen(false);
  };

  return (
    <nav
      className={`${styles.navbar} ${isSticky ? styles.sticky : ""} ${
        i18n.language === "ar" ? styles.langAr : styles.langEn
      }`}
    >
      <div className="container d-flex align-items-center justify-content-between">
        <Link
          className={styles.navbarBrand}
          to="/"
          onClick={() => setMenuOpen(false)}
        >
          <img src="/logo.png" alt="Takween Logo" />
        </Link>

        <ul className={`${styles.navbarNav} ${styles.desktopNav}`}>
          {NAV_LINKS.map((link) => (
            <li key={link.id} className="nav-item">
              <Link
                to={link.isPage ? link.path : `/#${link.id}`}
                className={styles.navLink}
                onClick={(e) => handleLinkClick(e, link)}
              >
                {t(link.label)}
              </Link>
            </li>
          ))}
        </ul>

        <div className="d-flex align-items-center gap-3">
          <Link
            to="/cart"
            className={styles.cartLink}
            onClick={() => setMenuOpen(false)}
          >
            <i className="bi bi-cart3"></i>
            {cartItemsCount > 0 && (
              <span className={styles.cartBadge}>{cartItemsCount}</span>
            )}
          </Link>

          {user ? (
            <div className="dropdown">
              <button
                className={`btn ${styles.customToggle} dropdown-toggle`}
                data-bs-toggle="dropdown"
              >
                <i className="bi bi-person-circle"></i>
              </button>
              <ul className={`dropdown-menu ${styles.dropdownMenuCustom}`}>
                <li>
                  <Link className="dropdown-item" to="/account">
                    {t("navbar.account", "My Account")}
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/my-books">
                    {t("navbar.myBooks", "My Books")}
                  </Link>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleLogout}>
                    {t("navbar.logout", "Logout")}
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            <Link to="/login" className={styles.loginLink}>
              {t("navbar.login", "Login")}
            </Link>
          )}

          <div className="dropdown">
            <button
              className={`btn ${styles.customToggle} dropdown-toggle`}
              data-bs-toggle="dropdown"
            >
              {i18n.language === "ar" ? "العربية" : "English"}
            </button>
            <ul className={`dropdown-menu ${styles.dropdownMenuCustom}`}>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => changeLanguage("ar")}
                >
                  {t("navbar.language_ar")}
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => changeLanguage("en")}
                >
                  {t("navbar.language_en")}
                </button>
              </li>
            </ul>
          </div>

          <button
            className={styles.navbarToggler}
            onClick={() => setMenuOpen(true)}
          >
            <i className="bi bi-list"></i>
          </button>
        </div>

        <div className={`${styles.mobileMenu} ${menuOpen ? styles.show : ""}`}>
          <button
            className={styles.closeMobileMenu}
            onClick={() => setMenuOpen(false)}
          >
            &times;
          </button>
          <ul className={styles.mobileNavList}>
            {NAV_LINKS.map((link) => (
              <li key={link.id}>
                <Link
                  to={link.isPage ? link.path : `/#${link.id}`}
                  onClick={(e) => handleLinkClick(e, link)}
                >
                  {t(link.label)}
                </Link>
              </li>
            ))}

            <li>
              <Link to="/cart" onClick={() => setMenuOpen(false)}>
                {t("navbar.cart", "Cart")}
                {cartItemsCount > 0 ? " (" + cartItemsCount + ")" : ""}
              </Link>
            </li>

            {user ? (
              <>
                <li>
                  <Link to="/account" onClick={() => setMenuOpen(false)}>
                    {t("navbar.account", "My Account")}
                  </Link>
                </li>
                <li>
                  <Link to="/my-books" onClick={() => setMenuOpen(false)}>
                    {t("navbar.myBooks", "My Books")}
                  </Link>
                </li>
                <li>
                  <button
                    onClick={handleLogout}
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      padding: 0,
                    }}
                  >
                    {t("navbar.logout", "Logout")}
                  </button>
                </li>
              </>
            ) : (
              <li>
                <Link to="/login" onClick={() => setMenuOpen(false)}>
                  {t("navbar.login", "Login")}
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default memo(Navbar);
